import { atom } from 'jotai';
import { atomWithReset, atomWithStorage, loadable } from 'jotai/utils';
import callApi from 'lib/callApi';
import { companyIdAtom } from 'views/state';

import {
  applyBPADashboardFilters,
  buildBPATopRankings,
} from './Charts/filterData';
import { BPADashboardData } from './types';
import { IntervalFilter, TimespanFilter } from '../types';

export const BPA_SEVERITY_VALUES = [4, 3, 2, 1] as const;

export const severityFiltersAtom = atomWithStorage<number[]>(
  'bpa_dashboard_severity_filter',
  [...BPA_SEVERITY_VALUES],
);

export const providerFiltersAtom = atomWithReset<string[]>([]);

export const showHistoryChartCountsAtom = atomWithStorage(
  'bpa_dashboard_show_counts',
  false,
);

export const timeSpanAtom = atomWithReset(TimespanFilter.SixMonth);
export const intervalAtom = atomWithReset(IntervalFilter.Week);
export const bpaRefreshAtom = atom(0);
export const historyChartsOpenAtom = atom(true);
export const currentChartsOpenAtom = atom(true);

export const appliedIntegrationIdsAtom = atomWithReset<number[]>([]);

export const dashboardSourceDataAtom = atom<
  Promise<BPADashboardData | undefined>
>(async (get) => {
  const companyId = get(companyIdAtom);
  const integrationIds = get(appliedIntegrationIdsAtom);
  void get(bpaRefreshAtom);

  if (!companyId || companyId === -1) return undefined;

  const response = await callApi('bpa/dashboard/', 'GET', {
    company_id: companyId,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    integration_ids: integrationIds.join(','),
    client_side_filters: true,
  });
  return response?.data as BPADashboardData;
});

export const dashboardSourceDataLoadableAtom = loadable(
  dashboardSourceDataAtom,
);

export const dashboardDataAtom = atom(async (get) => {
  const source = await Promise.resolve(get(dashboardSourceDataAtom));
  if (!source) return undefined;

  return applyBPADashboardFilters(source, {
    severity: get(severityFiltersAtom),
    provider: get(providerFiltersAtom),
    duration: get(timeSpanAtom),
    interval: get(intervalAtom),
  });
});

/**
 * Complete, filtered control and benchmark rankings used by the top charts and
 * their View Details dialogs. The regular dashboard response only exposes the
 * first ten groups under `current`; the canonical cells intentionally remain
 * available here so the dialogs can paginate every matching group locally.
 */
export const topChartRankingsAtom = atom(async (get) => {
  const source = await Promise.resolve(get(dashboardSourceDataAtom));
  if (!source?.filter_data) return { controls: [], benchmarks: [] };

  const selectedSeverity = get(severityFiltersAtom);
  const selectedProvider = get(providerFiltersAtom);
  const providers =
    selectedProvider.length === 1
      ? selectedProvider
      : source.filter_data.providers.map((item) => item.provider);
  return buildBPATopRankings(
    source.filter_data.top_chart_cells || [],
    providers,
    selectedSeverity,
  );
});
